import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Plus } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { DateRangePicker, DateRangeValue } from '../../ui/date-picker';
import { cn } from '../../../lib/utils';
import { MedalQuote, MedalMaterial } from '../../../types/medals';
import {
  MedalQuoteDetailsDialog,
  MedalQuoteMobileCard,
  MedalQuotesTable,
  formatCurrency,
  sortQuotes,
  calculateQuoteTotals,
  SortColumn,
  SortDirection
} from './index';
import { CreateQuoteDialog } from './CreateQuoteDialog';

interface MedalQuotesSectionProps {
  quotes: MedalQuote[];
  quotesLoading: boolean;
  accessToken?: string;
  materials: MedalMaterial[];
  products?: any[];
  onRefresh: () => Promise<void>;
  onUpdateStatus: (quoteId: string, status: string) => Promise<void>;
  onUpdateQuoteItem?: (itemId: string, data: { quantity: number }) => Promise<any>;
  onDeleteQuoteItem?: (itemId: string) => Promise<any>;
  onAddQuoteItem?: (quoteId: string, data: any) => Promise<any>;
  onMarkAsViewed?: (quoteId: string) => Promise<void>;
}

export function MedalQuotesSection({
  quotes,
  quotesLoading,
  accessToken,
  materials,
  products = [],
  onRefresh,
  onUpdateStatus,
  onUpdateQuoteItem,
  onDeleteQuoteItem,
  onAddQuoteItem,
  onMarkAsViewed,
}: MedalQuotesSectionProps) {
  const [quotesSearch, setQuotesSearch] = useState('');
  const [quotesFilter, setQuotesFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  // Default date filter to today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [quotesDateRange, setQuotesDateRange] = useState<DateRangeValue | undefined>({ from: today, to: undefined });

  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedQuote, setSelectedQuote] = useState<MedalQuote | null>(null);
  const [quoteDetailsOpen, setQuoteDetailsOpen] = useState(false);
  const [quoteStatusUpdating, setQuoteStatusUpdating] = useState(false);
  const [createQuoteDialogOpen, setCreateQuoteDialogOpen] = useState(false);

  // Sync selectedQuote with updated quotes data
  useEffect(() => {
    if (selectedQuote && quoteDetailsOpen) {
      const updated = quotes.find(q => q.id === selectedQuote.id);
      if (updated) {
        setSelectedQuote(updated);
      }
    }
  }, [quotes, selectedQuote, quoteDetailsOpen]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleOpenQuoteDialog = async (quote: MedalQuote) => {
    setSelectedQuote(quote);
    setQuoteDetailsOpen(true);

    // Mark as viewed if not already viewed
    if (!quote.isViewed && onMarkAsViewed) {
      await onMarkAsViewed(quote.id);
    }
  };

  const handleUpdateQuoteStatus = async (quoteId: string, status: string) => {
    setQuoteStatusUpdating(true);
    try {
      await onUpdateStatus(quoteId, status);
      // Update selected quote if it's the same
      if (selectedQuote?.id === quoteId) {
        setSelectedQuote({ ...selectedQuote, status });
      }
    } finally {
      setQuoteStatusUpdating(false);
    }
  };

  // Extract all unique quote dates for the calendar highlighting
  const allQuoteDates = useMemo(() => {
    return quotes.map(q => {
      const date = new Date(q.createdAt);
      date.setHours(0, 0, 0, 0);
      return date;
    });
  }, [quotes]);

  // Filter quotes by date range
  const dateFilteredQuotes = useMemo(() => {
    if (!quotesDateRange?.from) {
      return quotes;
    }

    return quotes.filter((quote) => {
      const quoteDate = new Date(quote.createdAt);
      quoteDate.setHours(0, 0, 0, 0);

      // quotesDateRange.from is guaranteed to exist due to the check above
      const from = new Date(quotesDateRange.from!);
      from.setHours(0, 0, 0, 0);

      if (!quotesDateRange.to) {
        // Single date selected
        return quoteDate.getTime() === from.getTime();
      }

      const to = new Date(quotesDateRange.to);
      to.setHours(23, 59, 59, 999);

      return quoteDate >= from && quoteDate <= to;
    });
  }, [quotes, quotesDateRange]);

  // Filter quotes by search term
  const searchFilteredQuotes = useMemo(() => {
    if (!quotesSearch.trim()) {
      return dateFilteredQuotes;
    }
    const searchLower = quotesSearch.toLowerCase().trim();
    return dateFilteredQuotes.filter((quote) => {
      const createdDate = new Date(quote.createdAt);
      const dateStr = createdDate.toLocaleDateString('pt-BR');
      const isoDateStr = createdDate.toISOString().split('T')[0];

      return (
        quote.quoteNumber.toLowerCase().includes(searchLower) ||
        (quote.customerName && quote.customerName.toLowerCase().includes(searchLower)) ||
        (quote.customerEmail && quote.customerEmail.toLowerCase().includes(searchLower)) ||
        (quote.customerCompany && quote.customerCompany.toLowerCase().includes(searchLower)) ||
        (quote.customerPhone && quote.customerPhone.includes(searchLower)) ||
        dateStr.includes(searchLower) ||
        isoDateStr.includes(searchLower)
      );
    });
  }, [dateFilteredQuotes, quotesSearch]);

  // Filter quotes by status
  const filteredQuotes = useMemo(() => {
    if (quotesFilter === 'all') return searchFilteredQuotes;
    return searchFilteredQuotes.filter(q => q.status === quotesFilter);
  }, [searchFilteredQuotes, quotesFilter]);

  const sortedQuotes = useMemo(() => {
    return sortQuotes(filteredQuotes, sortColumn, sortDirection);
  }, [filteredQuotes, sortColumn, sortDirection]);

  const quoteTotals = useMemo(() => {
    return calculateQuoteTotals(searchFilteredQuotes);
  }, [searchFilteredQuotes]);

  return (
    <>
      <Card className="shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle>Orcamentos de Medalhas</CardTitle>
            <CardDescription>
              Visualize e gerencie os orcamentos de medalhas enviados pelos clientes.
            </CardDescription>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Input
              placeholder="Buscar por numero, cliente..."
              value={quotesSearch}
              onChange={(event) => setQuotesSearch(event.target.value)}
              className="w-full sm:w-64"
            />
            <DateRangePicker
              dateRange={quotesDateRange}
              onDateRangeChange={setQuotesDateRange}
              highlightedDates={allQuoteDates}
              placeholder="Filtrar por data"
              className="w-full sm:w-auto"
            />
            <div className="flex gap-2">
              <Button type="button" onClick={onRefresh} disabled={quotesLoading} className="flex-1 sm:flex-initial">
                <RefreshCw className={cn('mr-2 h-4 w-4', quotesLoading && 'animate-spin')} />
                <span className="hidden md:inline">{quotesLoading ? 'Atualizando' : 'Recarregar'}</span>
                <span className="md:hidden">Atualizar</span>
              </Button>
              <Button
                type="button"
                onClick={() => setCreateQuoteDialogOpen(true)}
                className="flex-1 sm:flex-initial"
              >
                <Plus className="mr-2 h-4 w-4" />
                <span className="hidden md:inline">Criar</span>
                <span className="md:hidden">Criar</span>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Totals Summary */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm border-b pb-3">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Total:</span>
              <span className="font-semibold">{formatCurrency(quoteTotals.all)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-yellow-600">Pendentes:</span>
              <span className="font-medium text-yellow-600">{formatCurrency(quoteTotals.pending)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">Aprovados:</span>
              <span className="font-medium text-green-600">{formatCurrency(quoteTotals.approved)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-red-600">Rejeitados:</span>
              <span className="font-medium text-red-600">{formatCurrency(quoteTotals.rejected)}</span>
            </div>
          </div>

          <Tabs value={quotesFilter} onValueChange={(value) => setQuotesFilter(value as 'all' | 'pending' | 'approved' | 'rejected')}>
            <TabsList>
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="pending">Pendentes</TabsTrigger>
              <TabsTrigger value="approved">Aprovados</TabsTrigger>
              <TabsTrigger value="rejected">Rejeitados</TabsTrigger>
            </TabsList>
            <TabsContent value={quotesFilter}>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {quotesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : sortedQuotes.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    Nenhum orcamento encontrado.
                  </p>
                ) : (
                  sortedQuotes.map((quote: MedalQuote) => (
                    <MedalQuoteMobileCard
                      key={quote.id}
                      quote={quote}
                      onOpenDetails={handleOpenQuoteDialog}
                    />
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              {quotesLoading ? (
                <div className="hidden md:flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <MedalQuotesTable
                  quotes={sortedQuotes}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  onOpenDetails={handleOpenQuoteDialog}
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Quote Details Dialog */}
      <MedalQuoteDetailsDialog
        quote={selectedQuote}
        open={quoteDetailsOpen}
        onOpenChange={setQuoteDetailsOpen}
        quoteStatusUpdating={quoteStatusUpdating}
        onUpdateStatus={handleUpdateQuoteStatus}
        materials={materials}
        products={products}
        accessToken={accessToken}
        onCustomItemPriceConfirmed={onRefresh}
        onQuoteUpdated={onRefresh}
        onUpdateQuoteItem={onUpdateQuoteItem}
        onDeleteQuoteItem={onDeleteQuoteItem}
        onAddQuoteItem={onAddQuoteItem}
      />

      {/* Create Quote Dialog */}
      <CreateQuoteDialog
        open={createQuoteDialogOpen}
        onOpenChange={setCreateQuoteDialogOpen}
        accessToken={accessToken}
        onQuoteCreated={async (newQuote) => {
          await onRefresh();
          setCreateQuoteDialogOpen(false);
          setSelectedQuote(newQuote);
          setQuoteDetailsOpen(true);
        }}
      />
    </>
  );
}
